import { getFeature, getQuoteLoanApplication } from '@paintscout/util/builder'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import { useConnection } from 'react-detect-offline'
import type { LoanApplicationStatus, SignupStatus } from '@paintscout/wisetack'
import { useQuote } from '../context'
import { MIN_PROMO_BALANCE, useWisetack } from './useWisetack'

export interface WisetackSectionVisibility {
  /** Show the "as low as $X/month*" line. */
  showPromo: boolean
  /** Show the compact status + apply row (WisetackApplicationNext). */
  showApplication: boolean
  /** The customer's loan application status, when they have one. */
  loanStatus?: LoanApplicationStatus
}

/**
 * Single gate for the Wisetack block inside the estimateNext total table (promo line + compact
 * application row). Both surfaces live in one bordered wrapper, so the wrapper needs to know
 * whether *either* has something to say before it draws its border.
 *
 * The promo and the application row share the same conditions — a declined application keeps
 * both, because Wisetack lets the customer apply again. WisetackPromo keeps its own copy of
 * this logic for the legacy quote view, where a declined application still hides it in the
 * customer-facing document — change both together.
 */
export function useWisetackSection(): WisetackSectionVisibility {
  const { options } = useClientOptions()
  const { user, preferences } = useUser()
  const { online } = useConnection()
  const { quote, wisetackPromo, isPresentation } = useQuote()
  const { hasWisetack, merchantSignupDate, showOnOldDocuments } = useWisetack({ options, user, preferences })

  const contact = quote?.contact
  const loanStatus = getQuoteLoanApplication({ contact, quote })?.changedStatus as LoanApplicationStatus | undefined
  const signupStatus = options?.options?.wisetackSignup?.status as SignupStatus | undefined

  // Don't advertise financing on documents created before the merchant signed up, unless the
  // merchant opted in to showing it there.
  const isOldDocument = !!merchantSignupDate && merchantSignupDate.isAfter(quote?.dates?.created) && !showOnOldDocuments

  const show =
    !!getFeature({ options, preferences, path: 'integrations.providers.wisetack' }) &&
    hasWisetack &&
    online &&
    !!wisetackPromo &&
    !wisetackPromo.error &&
    !isOldDocument &&
    !contact?.company &&
    signupStatus !== 'APPLICATION_DECLINED' &&
    quote?.totals?.balance_due > MIN_PROMO_BALANCE &&
    // The customer-facing document has nobody to apply, and no promo to personalize.
    !(isPresentation && !contact)

  return {
    showPromo: show,
    // The application row needs the signup date to decide what the merchant can do next.
    showApplication: show && !!merchantSignupDate,
    loanStatus
  }
}
